import { useState } from "react";
import { useIntl } from "react-intl";
import { IScopeBase } from "@cayo/shared/api/types/schema";
import { showError } from "@cayo/shared/components/App/utils/showError";
import ajax from "@cayo/shared/libs/ajax";
import useService from "@cayo/shared/services/hooks/useService";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { globalObjectSearchMessages } from "./messages";
import { ILookupResultItem } from "../../../../api/schema.api";
import { modelUtils } from "../../../../components/Form/modelUtils/utils";
import { IMembersLookupData } from "../types";

export const useGlobalObjectSearch = (
  lookupScope: IScopeBase,
  membersLookupData: IMembersLookupData,
  onAddObjects: (items: any[]) => void
) => {
  const actionBuilder = useService("actionBuilder");
  const { uiInteraction } = actionBuilder;

  const { formatMessage } = useIntl();

  const [globalSearch, setGlobalSearch] = useState({
    scope: membersLookupData?.SearchScope,
    string: membersLookupData?.SearchString,
  });

  const setSearchScope = (scope: string) => setGlobalSearch((prev) => ({ ...prev, scope }));
  const setSearchString = (string: string) => setGlobalSearch((prev) => ({ ...prev, string }));

  const [dialogsQueue, setDialogsQueue] = useState<ILookupResultItem[]>([]);
  const activeDialog = dialogsQueue.length ? dialogsQueue[0] : null;

  const dismissDialog = () => {
    setDialogsQueue((prev) => prev.slice(1));
  };

  const dismissAllDialogs = () => {
    setDialogsQueue([]);
  };

  const fetchGlobalSearch = async () => {
    try {
      uiInteraction.setLoading(true);

      const values = globalSearch.string
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean);

      const parameters = [
        ...lookupScope.body.parameters,
        ...modelUtils.convertToNameValueDictionary2({
          CayoLookupScope: globalSearch.scope,
          CayoLookupValue: JSON.stringify(values),
        }),
      ];

      const res: ILookupResultItem[] = await ajax.getClient(lookupScope.path, "POST", undefined, {
        parameters,
      });

      if (!res.length) {
        return;
      }

      // determine empty/full flags and collect items and objects
      let isAtLeastOneEmpty = false;
      let areAllEmpty = true;
      const lookupObjectsToAdd: any[] = [];
      const lookupItemsInDialog: ILookupResultItem[] = [];

      for (const obj of res) {
        const len = obj.lookupObjs.length;
        if (len === 0) {
          isAtLeastOneEmpty = true;
        } else {
          areAllEmpty = false;
          if (len === 1) {
            lookupObjectsToAdd.push(obj.lookupObjs[0]);
          } else {
            lookupItemsInDialog.push(obj);
          }
        }
      }

      if (areAllEmpty) {
        uiInteraction.alertError(formatMessage(globalObjectSearchMessages.noObjectsFound));
        return;
      }

      if (isAtLeastOneEmpty) {
        uiInteraction.alertError(formatMessage(globalObjectSearchMessages.noObjectsFound));
      }

      if (lookupObjectsToAdd.length) {
        onAddObjects(lookupObjectsToAdd);
      }

      if (lookupItemsInDialog.length) {
        setDialogsQueue(lookupItemsInDialog);
      }
    } catch (e) {
      const error = await ajaxUtils.getError(e);
      showError(error?.error_description);
    } finally {
      uiInteraction.setLoading(false);
    }
  };

  return {
    activeDialog,
    dismissDialog,
    dismissAllDialogs,
    globalSearch,
    setSearchScope,
    setSearchString,
    fetchGlobalSearch,
  };
};
